import { formatDurationLabel } from 'components/CallScribe/callLogColumns';
import type { ICallScribeCallTranscript } from 'services/callScribe/types';

const HIDDEN = new Set(['', 'unknown', 'und', 'null', 'undefined', 'auto']);
const LANGUAGE_CODE = /^[A-Za-z]{2,3}([-_][A-Za-z0-9]{2,8})*$/;
const LANGUAGE_WORD = /^\p{L}{4,}( \p{L}+)*$/u;

let names: Intl.DisplayNames | null | undefined;

const displayNames = (): Intl.DisplayNames | null => {
  if (names === undefined) {
    try {
      names = new Intl.DisplayNames(['en'], { type: 'language', fallback: 'none' });
    } catch {
      names = null;
    }
  }
  return names;
};

export const languageName = (raw: string | null | undefined): string | null => {
  const value = (raw ?? '').trim();
  if (HIDDEN.has(value.toLowerCase())) return null;
  if (LANGUAGE_CODE.test(value)) {
    try {
      return displayNames()?.of(value.replace(/_/g, '-')) ?? null;
    } catch {
      return null;
    }
  }
  if (LANGUAGE_WORD.test(value)) {
    return value.toLowerCase().replace(/(^|\s)\p{L}/gu, letter => letter.toUpperCase());
  }
  return null;
};

export const transcriptCaption = (
  transcript: Pick<ICallScribeCallTranscript, 'language' | 'wordCount' | 'durationSeconds'>,
): string => {
  const parts: string[] = [];
  const language = languageName(transcript.language);
  if (language) parts.push(language);
  const words = transcript.wordCount;
  if (typeof words === 'number' && words > 0) {
    parts.push(`${words.toLocaleString('en-US')} ${words === 1 ? 'word' : 'words'}`);
  }
  const seconds = transcript.durationSeconds;
  if (typeof seconds === 'number' && seconds > 0) {
    parts.push(`${formatDurationLabel(seconds)} of audio`);
  }
  return parts.join(' · ');
};
